import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Chip, Paper, Grid, Divider, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Skeleton,
  TablePagination,
} from "@mui/material";
import {
  Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { apiPath, apiSharedCampaignReport } from "../../apiPath";

ChartJS.register(ArcElement, ChartTooltip, Legend);

const TEAL = "#0D9A8F";
const PIE_COLORS = ["#0D9A8F","#2196F3","#FF9800","#9C27B0","#E82A2A","#4CAF50","#00BCD4","#FF5722","#795548","#607D8B"];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ── Mini Pie ──────────────────────────────────────────────────────────────────

const MiniPie = ({ title, labels, counts, colors }) => {
  const data = {
    labels,
    datasets: [{ data: counts, backgroundColor: colors || PIE_COLORS.slice(0, labels.length), borderWidth: 1, borderColor: "#fff" }],
  };
  const options = {
    responsive: true,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
    },
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>{title}</Typography>
      {labels.length === 0
        ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No data</Typography>
        : <Box sx={{ maxWidth: 240, mx: "auto" }}><Pie data={data} options={options} /></Box>
      }
    </Paper>
  );
};

// ── Page Skeleton ─────────────────────────────────────────────────────────────

const PageSkeleton = () => (
  <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
    <Box sx={{ bgcolor: TEAL, px: { xs: 2, md: 5 }, py: 2.5 }}>
      <Skeleton variant="text" width={280} height={30} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
    </Box>
    <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
      <Grid container spacing={2}>
        {[1,2,3].map((i) => <Grid item xs={12} md={4} key={i}><Skeleton variant="rounded" height={200} /></Grid>)}
      </Grid>
      <Skeleton variant="rounded" height={300} sx={{ mt: 2 }} />
    </Box>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────

const SharedReport = () => {
  const { token } = useParams();
  const [loading, setLoading]     = useState(true);
  const [expired, setExpired]     = useState(false);
  const [notFound, setNotFound]   = useState(false);
  const [report, setReport]       = useState(null);
  const [summary, setSummary]     = useState(null);
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);       // 0-indexed (TablePagination)
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [tableLoading, setTableLoading] = useState(false);

  const fetchPage = useCallback(async (pg, rpp) => {
    if (!token) return;
    setTableLoading(true);
    try {
      const params = new URLSearchParams({ page: pg + 1, pageSize: rpp });
      const res  = await fetch(`${apiPath}${apiSharedCampaignReport}/${token}?${params}`);
      const json = await res.json();
      if (json.status === "success") {
        setData(json.data.data || []);
        setTotal(json.data.total || 0);
        setPage(pg);
        setRowsPerPage(rpp);
      }
    } finally {
      setTableLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    fetch(`${apiPath}${apiSharedCampaignReport}/${token}?page=1&pageSize=20`)
      .then((r) => {
        if (r.status === 410) { setExpired(true); setLoading(false); return null; }
        if (r.status === 404) { setNotFound(true); setLoading(false); return null; }
        return r.json();
      })
      .then((json) => {
        if (!json) return;
        if (json.status === "success") {
          setReport(json.data.report);
          setSummary(json.data.summary);
          setData(json.data.data || []);
          setTotal(json.data.total || 0);
        } else if (json.status === "expired") {
          setExpired(true);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [token]);

  if (loading) return <PageSkeleton />;

  if (expired) return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="h5" fontWeight={700} color="error" mb={1}>Report Link Expired</Typography>
      <Typography color="text.secondary">This shared report link is no longer active.</Typography>
    </Box>
  );

  if (notFound) return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Report Not Found</Typography>
      <Typography color="text.secondary">The link you followed does not point to a valid report.</Typography>
    </Box>
  );

  // Chart data from server summary
  const segEntries    = (summary?.segments || []).map((s) => [s.label, s.count]);
  const registered    = summary?.registered?.yes ?? 0;
  const notRegistered = summary?.registered?.no  ?? 0;
  const called        = summary?.called?.yes      ?? 0;
  const notCalled     = summary?.called?.no       ?? 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box sx={{ bgcolor: TEAL, px: { xs: 2, md: 5 }, py: 2.5, color: "#fff" }}>
        <Typography variant="h6" fontWeight={700}>{report.campaignName} — Campaign Report</Typography>
        <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
          <Chip size="small" label={report.reportType === "called" ? "Called Report" : "Overall Report"}
            sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 600, height: 20, fontSize: 11 }} />
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {fmtDate(report.dateFrom)} – {fmtDate(report.dateTo)}
            {report.timeFrom ? `  ·  ${report.timeFrom}–${report.timeTo}` : ""}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Generated by {report.pmName}  ·  Expires {fmtDate(report.expiresAt)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 3 }}>
        {/* Summary chips */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
          <Chip label={`${summary?.total ?? 0} total contacts`} size="small" sx={{ bgcolor: "#f1f5f9" }} />
          <Chip label={`${registered} registered`} size="small" sx={{ bgcolor: "#dcfce7", color: "#166534" }} />
          <Chip label={`${called} called`} size="small" sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }} />
        </Box>

        {/* Charts — hidden */}
        {/* <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <MiniPie
              title="BY COMPANY SEGMENT"
              labels={segEntries.map((e) => e[0])}
              counts={segEntries.map((e) => e[1])}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MiniPie
              title="REGISTERED STATUS"
              labels={["Registered", "Not Registered"]}
              counts={[registered, notRegistered]}
              colors={["#4CAF50", "#e2e8f0"]}
            />
          </Grid>
          {report.reportType === "overall" && (
            <Grid item xs={12} sm={4}>
              <MiniPie
                title="CALLED vs NOT CALLED"
                labels={["Called", "Not Called"]}
                counts={[called, notCalled]}
                colors={["#2196F3", "#e2e8f0"]}
              />
            </Grid>
          )}
        </Grid> */}

        {/* Data Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "#f8fafc" }}>
            <Typography variant="body2" fontWeight={700}>Contact Data ({total} records)</Typography>
          </Box>
          <Box sx={{ overflowX: "auto", position: "relative" }}>
            {tableLoading && (
              <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                <CircularProgress size={24} sx={{ color: "#0D9A8F" }} />
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Contact Name","Job Title","Company","Segment","Call Remarks","Registered"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, bgcolor: "#edf7f6", color: "#0D9A8F" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>No data available</TableCell>
                    </TableRow>
                  )
                  : data.map((r) => (
                    <TableRow key={r._id} hover>
                      <TableCell>{r.Full_Name || "—"}</TableCell>
                      <TableCell>{r.Job_Title || "—"}</TableCell>
                      <TableCell>{r.Company_Name || "—"}</TableCell>
                      <TableCell>{r.Company_Segment || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.lastRemarks || "—"}
                          sx={{ height: 18, fontSize: 11,
                            bgcolor: r.lastRemarks === "Interested" ? "#dcfce7"
                              : r.lastRemarks === "Not Interested" ? "#fee2e2"
                              : "#f1f5f9",
                            color: r.lastRemarks === "Interested" ? "#166534"
                              : r.lastRemarks === "Not Interested" ? "#991b1b"
                              : "#334155",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.isRegistered ? "Yes" : "No"}
                          sx={{ height: 18, fontSize: 11,
                            bgcolor: r.isRegistered ? "#dcfce7" : "#f1f5f9",
                            color: r.isRegistered ? "#166534" : "#64748b",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(_, newPage) => fetchPage(newPage, rowsPerPage)}
            onRowsPerPageChange={(e) => fetchPage(0, +e.target.value)}
          />
        </Paper>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.disabled">
          This is a read-only view. Data reflects the campaign state at the time this link is accessed.
          Link expires on {fmtDate(report.expiresAt)}.
        </Typography>
      </Box>
    </Box>
  );
};

export default SharedReport;
